import React, { useMemo, useRef } from 'react';

import { BoardStarsQuery } from './BoardStarsQuery.generated';
import { StarredBoardsListItemSkeleton } from './StarredBoardsListItemSkeleton';
import { StarredBoardsListPageQuery } from './StarredBoardsListPageQuery.generated';
import { useHasBeenInViewport } from './useIsInViewport';
import { useStarredBoardsListPage } from './useStarredBoardsListPage';
import { useStarredBoardsQueryErrorHandlingEffect } from './useStarredBoardsQueryErrorHandlingEffect';

type Board = NonNullable<StarredBoardsListPageQuery['boards']>[number];
type BoardStar = NonNullable<BoardStarsQuery['member']>['boardStars'][number];

interface BoardsListItemLoaderProps {
  board?: Board;
  boardStar: BoardStar;
  isLoading?: boolean;
  renderBoard: (props: { board?: Board; boardStar: BoardStar }) => JSX.Element;
}

export const BoardsListItemLoader: React.FC<BoardsListItemLoaderProps> = ({
  board,
  boardStar,
  isLoading,
  renderBoard,
}) => {
  if (isLoading) {
    return <StarredBoardsListItemSkeleton />;
  }
  return renderBoard({ board, boardStar });
};

interface StarredBoardsListPageProps {
  boardStars: BoardStar[];
  renderBoard: (props: { board?: Board; boardStar: BoardStar }) => JSX.Element;
}

export const StarredBoardsListPage: React.FC<StarredBoardsListPageProps> = ({
  boardStars,
  renderBoard,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // Use this hook instead of `useIsInViewport` because otherwise
  // we'll re-query Apollo every time the boards go in and out of the
  // viewport, causing a brief loading state where `data` changes to
  // `undefined` and back, which breaks dragging and dropping if a
  // board moves by more than 1 "page" in the list
  const hasBeenOnScreen = useHasBeenInViewport(ref);
  const skip = !hasBeenOnScreen;

  const boardIds = useMemo(
    () => boardStars.map((boardStar) => boardStar.idBoard),
    [boardStars],
  );

  const { data, error, loading } = useStarredBoardsListPage({ boardIds, skip });

  useStarredBoardsQueryErrorHandlingEffect(error);

  return (
    <div ref={ref}>
      {boardStars.map((boardStar) => (
        <BoardsListItemLoader
          key={boardStar.idBoard}
          board={data?.boards?.find((b) => boardStar.idBoard === b.id)}
          boardStar={boardStar}
          isLoading={loading || skip}
          renderBoard={renderBoard}
        />
      ))}
    </div>
  );
};
